"use client";

import * as NavigationMenu from "@radix-ui/react-navigation-menu";
import { ChevronDown, ChevronRight } from "lucide-react";
import { cn } from "@/lib/utils";
import "./nav-ulife.css";
import Link from "next/link";
import Image from "next/image";
import { useEffect, useState } from "react";
import { NavLink } from "./nav-link";
import type { WebNavigationMenu, WebNavigationComponent } from "@/util/query/navigation-query";
import {
    readGeneralOptions,
    readCollectionOptions,
    readProductOptions,
    type GeneralDisplayOptions as GeneralDisplayOptionsType,
    type CollectionDisplayOptions as CollectionDisplayOptionsType,
    type ProductDisplayOptions as ProductDisplayOptionsType,
    webNavigationOptionsType,
    readWebNavigationOptions,
} from "@/util/navigation-options";
import { encodeDynamicParts, getLinkTarget } from "@/util/util";
import ColorStyleContext from "@/lib/providers/color-style-privider";
import { useContext } from "react";
import { usePathname } from "next/navigation";
import { makeMenuComponentId as makeComponentId } from "@/util/make-component-id";

interface WebNavigationMenuWithProducts extends WebNavigationMenu {
    collectionProductMenus?: WebNavigationMenu[];
}

type Props = {
    menu: WebNavigationMenu; // specific top-level menu (Level 1)
    value: string;
    setValue: (value: string) => void;
    webNavigation?: WebNavigationComponent; // full navigation component (needed for global options like menuShowOptionWeb)
};

type AttbProps = {
    menu: WebNavigationMenu;
};

export const DisplayAttributes = ({ menu }: AttbProps) => {
    const currentPath = usePathname();
    const colorStyle = useContext(ColorStyleContext);
    if (!menu.attributes || menu.attributes.length === 0) {
        return null;
    }

    const selectedAtb = menu.attributes[0];
    if (menu.type === "TEXT" || menu.type === "MASTER") {
        return (
            <NavigationMenu.Item
                id={makeComponentId(1, "menu", "menu-attb")}
                className="list-none dynamic-color flex items-center"
                style={
                    {
                        "--dynamic-color": colorStyle?.menuTextNormal || "#3d3d3d",
                        "--dynamic-hover-color": colorStyle?.menuTextHover || "#254b7c",
                        "--dynamic-focus-color": colorStyle?.menuTextHover || "#254b7c",
                    } as React.CSSProperties
                }>
                <NavLink
                    id={makeComponentId(1, "menu", "menu-attb-link")}
                    href={encodeDynamicParts(selectedAtb.linkUrl)}
                    target={getLinkTarget(menu.linkTarget || "SELF")}
                    style={{
                        fontSize: "var(--general-font-size, 18px)",
                    }}
                    className={cn(
                        "flex gap-1 items-center font-light focus:font-bold dynamic-color transition-colors",
                        currentPath === selectedAtb.linkUrl && "font-bold",
                    )}>
                    {selectedAtb.displayName}
                </NavLink>
            </NavigationMenu.Item>
        );
        // } else if (menu.type === "ICON") {
        //     return (
        //         <NavigationMenu.Item className="list-none">
        //             <NavLink href={selectedAtb.linkUrl} target={getLinkTarget(menu.linkTarget || "SELF")}>
        //                 {selectedAtb.displayName}
        //                 <div className="relative">
        //                     <Image
        //                         src={selectedAtb.imageUrl}
        //                         alt={selectedAtb.imageAlt}
        //                         width={0}
        //                         height={0}
        //                         style={{ width: "auto", height: "30px" }}
        //                         unoptimized
        //                     />
        //                 </div>
        //             </NavLink>
        //         </NavigationMenu.Item>
        //     );
    } else {
        return null;
    }
};

// Display option types and helpers (parsed from backend fields)
type CollectionDisplayOptions = CollectionDisplayOptionsType;
type ProductDisplayOptions = ProductDisplayOptionsType;
type GeneralDisplayOptions = GeneralDisplayOptionsType;
type webNavigationOptions = webNavigationOptionsType;

function getWebNavigationOptions(webNav: WebNavigationComponent): webNavigationOptions {
    return readWebNavigationOptions(webNav.webNavigationOption);
}

function getGeneralDisplayOptions(menu: WebNavigationMenuWithProducts): GeneralDisplayOptions {
    return readGeneralOptions(menu);
}

function gridColsClassFromNumber(n: number | undefined): string {
    switch (n) {
        case 2:
            return "grid-cols-2";
        case 3:
            return "grid-cols-3";
        case 5:
            return "grid-cols-5";
        case 6:
            return "grid-cols-6";
        case 4:
        default:
            return "grid-cols-4";
    }
}

function normalizeBorderStyle(style: any): "circle" | "rectangle" | "plain" {
    if (style === "circle" || style === "rectangle") return style;
    // return "plain"; // default
    return "rectangle"; // default, changed to rectangle for better UX
}

function getCollectionDisplayOptions(menu: WebNavigationMenuWithProducts): CollectionDisplayOptions {
    const opts = readCollectionOptions(menu);
    return {
        showProductsInCollection: opts.showProductsInCollection ?? false,
        collectionGridColumns: (opts.collectionGridColumns as number) ?? 4,
        limitProductsInCollection: opts.limitProductsInCollection,
        showTextOnly: opts.showTextOnly ?? (!opts.showProductsInCollection && !opts.showTextAndImages),
        showTextAndImages: opts.showTextAndImages ?? false,
        productBorderStyle: normalizeBorderStyle(opts.productBorderStyle),
        contentDisplay: (opts as any).contentDisplay, // TEXT_ONLY | TEXT_AND_IMAGE
        textStyle: opts.textStyle ?? "regular",
    } as CollectionDisplayOptions;
}

function getProductDisplayOptions(menu: WebNavigationMenuWithProducts): ProductDisplayOptions {
    const opts = readProductOptions(menu);
    return {
        showTextOnly: opts.showTextOnly ?? true,
        showTextAndImages: opts.showTextAndImages ?? false,
        productBorderStyle: normalizeBorderStyle(opts.productBorderStyle),
        contentDisplay: (opts as any).contentDisplay,
        textStyle: opts.textStyle ?? "regular",
    } as ProductDisplayOptions;
}

function getThumbStyles(style: "circle" | "rectangle" | "plain", colorStyle?: any) {
    switch (style) {
        case "circle":
            return {
                wrapper: `relative flex items-center justify-center w-[120px] h-[120px] rounded-full overflow-hidden bg-white border ${
                    colorStyle?.border || "border-gray-200"
                }`,
                imgClass: "object-cover",
                imgStyle: { width: "100%", height: "100%" } as React.CSSProperties,
            };
        case "rectangle":
            return {
                wrapper:
                    "relative w-full max-w-[240px] flex p-2 justify-center h-auto bg-white rounded-[8px] border border-gray-100",
                imgClass: "object-contain",
                imgStyle: { width: "auto", height: "120px" } as React.CSSProperties,
            };
        case "plain":
        default:
            return {
                wrapper: "relative w-auto h-auto",
                imgClass: "object-contain",
                imgStyle: { width: "auto", height: "120px" } as React.CSSProperties,
            };
    }
}

// New: Resolve section title and font style from sectionTitleAttribute
function resolveSectionTitleAttributes(
    opts: any,
    defaultName: string,
    pathFirst?: string,
): { name: string; fontStyle: string } {
    try {
        const raw: any = (opts as any).sectionTitleAttribute;
        if (Array.isArray(raw) && raw.length > 0) {
            const match = raw.find(
                (r: any) => r && typeof r === "object" && r.loc && r.name && r.loc.toLowerCase() === pathFirst,
            );
            const chosen = match || raw[0];
            const font = (chosen?.fontStyle || (opts as any).sectionTitleTextStyle || "regular").toLowerCase();
            return { name: chosen?.name || defaultName, fontStyle: font };
        }
    } catch {}
    const fallbackStyle = ((opts as any)?.sectionTitleTextStyle || "regular").toLowerCase();
    return { name: defaultName, fontStyle: fallbackStyle };
}

function sectionTitleWeightClass(style: string | undefined) {
    const key = (style || "regular").toLowerCase();
    return key === "bold"
        ? "font-semibold text-[18px]"
        : key === "regular"
          ? "font-normal text-[18px]"
          : "font-light text-[12px]";
}

export const DisplayMenuList = ({ menu, value, setValue, webNavigation }: Props) => {
    const currentPath = usePathname();
    const selectedAtb = menu.attributes[0];
    // Treat an item as true ICON only when it has an image; otherwise render as TEXT to avoid empty icon boxes
    const isIconWithImage = (m: any) => m?.type === "ICON" && !!m?.attributes?.[0]?.imageUrl;
    const IsIconType = (menu.subMenuList as any[]).some(isIconWithImage);
    const haveTextType = (menu.subMenuList as any[]).some((value) => !isIconWithImage(value));
    const [selectedMenu, setSelectedMenu] = useState<WebNavigationMenu | null>(null);
    const [selectedSubMenu, setSelectedSubMenu] = useState<WebNavigationMenu[] | null>(null);
    const [open, setOpen] = useState(false);
    // Prevent re-auto-selecting first Level-2 after user manually collapses current selection
    const [autoSelectLocked, setAutoSelectLocked] = useState(false);
    // Track click for instant feedback on non-chevron (no submenu) Level 1 items. Persistence after navigation
    // relies on currentPath match; this state is only for pre-navigation immediate UI feedback.
    const [clickedLevel1Id, setClickedLevel1Id] = useState<string | null>(null);
    const colorStyle = useContext(ColorStyleContext);
    // Global (component-level) options live on the root WebNavigationComponent, not individual menus.
    // Fallback to menu for backward compatibility if webNavigation not provided.
    const webNavComponentOpts = getWebNavigationOptions(webNavigation as WebNavigationComponent);
    const generalMenuOpts = getGeneralDisplayOptions(menu as WebNavigationMenuWithProducts);
    // Compute padding classes for section titles based on preset + text style rules
    function computePadding(preset: string | undefined, textStyle: string | undefined, menuType?: string | undefined) {
        const presetUpper = (preset || "COMPACT").toUpperCase();
        const typeUpper = (menuType || "").toUpperCase();
        // TEXT / ICON / ICON_GROUP fixed by preset
        if (typeUpper === "TEXT" || typeUpper === "ICON" || typeUpper === "ICON_GROUP") {
            return presetUpper === "COMFORTABLE" ? "pl-0" : "pl-4";
        }

        const presetStyle = (textStyle || "light").toLowerCase();
        if (presetUpper === "COMFORTABLE") {
            return ""; // no padding for comfortable preset by default
        }
        // COMPACT: regular -> pl-4, light/bold -> pl-2
        if (presetStyle === "regular") return "pl-4";
        return "pl-2";
    }
    // Derive language code from pathname (expects pattern /en/... or /th/...) for choosing sectionTitleName index
    const pathFirst = (currentPath || "").split("/").filter(Boolean)[0]?.toLowerCase();
    const level2Section = resolveSectionTitleAttributes(generalMenuOpts, selectedAtb.displayName, pathFirst);
    const level2PadClass = computePadding(webNavComponentOpts.level2PaddingPreset, level2Section.fontStyle, undefined);
    const sectionTitle = (() => {
        // Prefer new sectionTitleAttribute if present
        const maybe = resolveSectionTitleAttributes(generalMenuOpts, selectedAtb.displayName, pathFirst);
        if (maybe?.name) return maybe.name;
        // Fallback to legacy sectionTitleName behavior
        const raw = (generalMenuOpts as any).sectionTitleName;
        if (!Array.isArray(raw) || raw.length === 0) return selectedAtb.displayName;
        if (typeof raw[0] === "string") {
            if (pathFirst === "en") return raw[0] || selectedAtb.displayName;
            if (pathFirst === "th") return raw[1] || raw[0] || selectedAtb.displayName;
            return raw[0] || selectedAtb.displayName;
        }
        const match = raw.find((r: any) => typeof r === "object" && r?.loc?.toLowerCase() === pathFirst);
        if (match && match.name) return match.name;
        const firstWithName = raw.find((r: any) => r && r.name);
        return (firstWithName && firstWithName.name) || selectedAtb.displayName;
    })();
    const showLevel2Header = generalMenuOpts.showSectionTitle !== false;
    // Broaden header condition: if Level 1 is MASTER/PRODUCT, show header even when Level 2 are all ICONs
    const shouldShowLevel2Header =
        showLevel2Header && (Boolean(haveTextType)|| menu.type === "TEXT" || menu.type === "MASTER" || (menu as any)?.routeType === "PRODUCT");

    type PanelData = {
        menu: WebNavigationMenuWithProducts;
        subMenuList: WebNavigationMenuWithProducts[];
    };
    const [panelCache, setPanelCache] = useState<Record<string, PanelData>>({});

    function groupSubMenuByType(subMenuList: WebNavigationMenu[]) {
        const groups: Array<{ type: "ICON_GROUP" | "TEXT" | "MASTER"; items: WebNavigationMenu[] }> = [];
        let currentIconGroup: WebNavigationMenu[] = [];

        for (const item of subMenuList) {
            const treatAsIcon = isIconWithImage(item); // only ICON with image stays ICON
            if (treatAsIcon) {
                currentIconGroup.push(item);
                continue;
            }
            // Flush accumulated ICONs before handling non-icon (or icon without image) item
            if (currentIconGroup.length > 0) {
                groups.push({ type: "ICON_GROUP", items: currentIconGroup });
                currentIconGroup = [];
            }
            if (item.type === "MASTER") {
                groups.push({ type: "MASTER", items: [item] });
            } else {
                // Any ICON without imageUrl is rendered as TEXT to prevent empty boxes
                groups.push({ type: "TEXT", items: [item] });
            }
        }
        if (currentIconGroup.length > 0) {
            groups.push({ type: "ICON_GROUP", items: currentIconGroup });
        }
        return groups;
    }

    // Handler to toggle menu open state on click
    const handleTriggerClick = (e: React.MouseEvent) => {
        e.preventDefault();
        setValue(isOpen ? "" : menu.id); // toggle open
    };

    const hasChevron = menu.subMenuList && menu.subMenuList.length > 0;
    const isOpen = value === menu.id;

    // If none of the Level-2 items have children, there is effectively no Level-3 panel
    const hasAnyLevel3 = Array.isArray(menu.subMenuList)
        ? (menu.subMenuList as WebNavigationMenuWithProducts[]).some(
              (sm) => Array.isArray(sm.subMenuList) && (sm.subMenuList as WebNavigationMenuWithProducts[]).length > 0,
          )
        : false;
    const level2RowWhenNoL3 = !hasAnyLevel3 && generalMenuOpts.flexDirection !== "column";

    useEffect(() => {
        if (!isOpen) {
            setPanelCache({});
            setSelectedMenu(null);
            setSelectedSubMenu(null);
            setAutoSelectLocked(false); // reset lock when Level 1 closes
        }
    }, [isOpen]);

    // Auto-open rule (revised): Only auto-open Level-3 if AND ONLY IF the FIRST Level-2 item has children.
    // If the first L2 has no children, do not auto-open any other L2 (avoid confusing UX).
    useEffect(() => {
        if (!isOpen || autoSelectLocked || selectedMenu) return;
        const subMenus = menu.subMenuList as WebNavigationMenuWithProducts[];
        if (!Array.isArray(subMenus) || subMenus.length === 0) return;
        const firstL2 = subMenus[0];
        const firstHasChildren = Array.isArray(firstL2.subMenuList) && (firstL2.subMenuList as any[]).length > 0;
        if (!firstHasChildren) return; // Do NOT auto-open anything if first L2 has no Level-3
        setSelectedMenu(firstL2);
        const children = firstL2.subMenuList as WebNavigationMenuWithProducts[];
        setSelectedSubMenu(children);
        setPanelCache((prev) =>
            prev[firstL2.id] ? prev : { ...prev, [firstL2.id]: { menu: firstL2, subMenuList: children } },
        );
    }, [isOpen, autoSelectLocked, selectedMenu, menu.subMenuList]);

    return (
        <NavigationMenu.Item className="list-none" value={menu.id} id={makeComponentId(1, "item", menu.id)}>
            {hasChevron ? (
                <NavigationMenu.Trigger
                    className="group"
                    id={makeComponentId(1, "trigger", menu.id)}
                    onClick={handleTriggerClick}
                    tabIndex={0}
                    aria-expanded={open}>
                    <span
                        id="Level1Nav"
                        className={cn(
                            "flex gap-1 items-center font-light cursor-pointer dynamic-color transition-colors",
                            (isOpen || clickedLevel1Id === menu.id) && "font-bold",
                        )}
                        style={
                            {
                                fontSize: "var(--general-font-size, 18px)",
                                "--dynamic-color": colorStyle?.menuTextNormal || "#3d3d3d",
                                "--dynamic-hover-color": colorStyle?.menuTextHover || "#254b7c",
                                "--dynamic-focus-color": colorStyle?.menuTextHover || "#254b7c",
                            } as React.CSSProperties
                        }>
                        {selectedAtb.displayName}
                        <ChevronDown
                            className={cn("h-3 w-3 transition-transform", isOpen ? "rotate-180" : "rotate-0")}
                        />
                    </span>
                </NavigationMenu.Trigger>
            ) : (
                <NavLink
                    id="Level1Nav"
                    href={encodeDynamicParts(selectedAtb.linkUrl || "")}
                    target={getLinkTarget(menu.linkTarget || "SELF")}
                    onMouseDown={() => setClickedLevel1Id(menu.id)}
                    onClick={() => {
                        setClickedLevel1Id(menu.id);
                    }}
                    className={cn(
                        "flex gap-1 items-center font-light focus:font-bold dynamic-color transition-colors",
                        (clickedLevel1Id === menu.id || currentPath === selectedAtb.linkUrl) && "font-bold",
                    )}
                    style={
                        {
                            fontSize: "var(--general-font-size, 18px)",
                            "--dynamic-color": colorStyle?.menuTextNormal || "#3d3d3d",
                            "--dynamic-hover-color": colorStyle?.menuTextHover || "#254b7c",
                            "--dynamic-focus-color": colorStyle?.menuTextHover || "#254b7c",
                        } as React.CSSProperties
                    }>
                    {selectedAtb.displayName}
                </NavLink>
            )}
            <NavigationMenu.Content
                id="Level1NavContent"
                forceMount
                className={cn(
                    "NavigationMenuContent nav-level2-panel flex w-full pl-2 pr-0 sm:px-10 sm:pr-0",
                    isOpen ? "block" : "hidden",
                    "transition-all duration-200 ease-in-out",
                )}
                style={
                    {
                        "--dynamic-nav-level2-bg":
                            // If a color key is provided by the CMS, pull the color from ColorStyleContext dynamically
                            webNavComponentOpts.level2BgColorKey &&
                            (colorStyle as any)?.[(webNavComponentOpts as any).level2BgColorKey]
                                ? (colorStyle as any)[(webNavComponentOpts as any).level2BgColorKey]
                                : webNavComponentOpts.customLevel2BgColor || "#FFFFFF",
                    } as React.CSSProperties
                }
                onMouseLeave={() => {
                    // Preserve selection & color until user explicitly collapses or closes Level 1.
                }}>
                <div
                    id={makeComponentId(2, "wrapper", menu.id)}
                    className={cn(
                        "NavigationMenuContentDiv flex w-full h-full mx-auto",
                        IsIconType && !haveTextType ? "flex-row" : "flex-row",
                        "transition-all duration-200 ease-in-out",
                    )}>
                    <div
                        id={makeComponentId(2, "container", menu.id)}
                        className={cn(
                            "flex justify-start items-start py-8 min-w-[300px] max-w-[300px] overflow-y-auto overflow-x-hidden custom-scrollbar",
                            // If no Level-3 exists, render Level-2 horizontally (row) unless CMS forces column
                            level2RowWhenNoL3
                                ? "flex-row flex-wrap gap-6 max-w-full"
                                : IsIconType && !haveTextType
                                  ? "flex-col gap-6"
                                  : "flex-col gap-2",
                        )}>
                        {shouldShowLevel2Header ? (
                            selectedAtb.linkUrl ? (
                                <NavLink
                                    id="Level2NavCategories"
                                    className={cn(
                                        sectionTitleWeightClass(level2Section.fontStyle),
                                        level2PadClass,
                                        "w-fit dynamic-color whitespace-normal truncate",
                                    )}
                                    style={
                                        {
                                            "--dynamic-color": colorStyle?.menuTextNormal || "#3d3d3d",
                                            "--dynamic-hover-color": colorStyle?.menuTextHover || "#254b7c",
                                            "--dynamic-focus-color": colorStyle?.menuTextHover || "#254b7c",
                                        } as React.CSSProperties
                                    }
                                    href={encodeDynamicParts(selectedAtb.linkUrl || "")}
                                    target={getLinkTarget(menu.linkTarget || "SELF")}>
                                    {sectionTitle}
                                </NavLink>
                            ) : (
                                <p
                                    className={cn(sectionTitleWeightClass(level2Section.fontStyle), level2PadClass)}
                                    style={{ color: colorStyle?.menuTextNormal || "#3d3d3d" }}>
                                    {sectionTitle}
                                </p>
                            )
                        ) : null}
                        <div
                            id={makeComponentId(2, "list", menu.id)}
                            className={cn(
                                // When no Level-3, show L2 items as a horizontal list (configurable)
                                level2RowWhenNoL3
                                    ? "flex flex-row flex-wrap gap-4 w-full"
                                    : "flex flex-col gap-2 w-fit min-w-[267px] max-w-[267px] align-middle",
                            )}>
                            {(menu.subMenuList as WebNavigationMenuWithProducts[]).map((subMenu) => {
                                // Original style extraction
                                const prodStyle = getProductDisplayOptions(
                                    subMenu as WebNavigationMenuWithProducts,
                                ).textStyle;
                                const collStyle = getCollectionDisplayOptions(
                                    subMenu as WebNavigationMenuWithProducts,
                                ).textStyle;
                                // New: derive unified content display mode (TEXT_ONLY | TEXT_AND_IMAGE)
                                const productOptsForDisplay = getProductDisplayOptions(
                                    subMenu as WebNavigationMenuWithProducts,
                                );
                                const collectionOptsForDisplay = getCollectionDisplayOptions(
                                    subMenu as WebNavigationMenuWithProducts,
                                );
                                // Also read sub-menu's own general options (may define sectionTitleAttribute individually)
                                const subGeneralOpts = readGeneralOptions(subMenu);
                                const subSection = resolveSectionTitleAttributes(
                                    subGeneralOpts,
                                    subMenu.attributes?.[0]?.displayName || "",
                                    pathFirst,
                                );
                                const contentDisplayMode = (
                                    productOptsForDisplay.contentDisplay ||
                                    collectionOptsForDisplay.contentDisplay ||
                                    ""
                                ).toUpperCase();
                                // Prefer collection style when this submenu represents a collection (routeType=COLLECTION or has collectionProductMenus)
                                const isCollectionL2 =
                                    (subMenu as any)?.routeType === "COLLECTION" ||
                                    !!(subMenu as any)?.collectionProductMenus?.length;
                                let resolvedStyle = collStyle || prodStyle;
                                if (isCollectionL2 && collStyle) {
                                    resolvedStyle = collStyle;
                                } else if (!isCollectionL2 && prodStyle) {
                                    resolvedStyle = prodStyle;
                                }
                                const textStyleKey = (
                                    resolvedStyle ||
                                    subSection.fontStyle ||
                                    "regular"
                                )?.toLowerCase();
                                const weightClass =
                                    textStyleKey === "bold"
                                        ? "font-semibold text-[18px]"
                                        : textStyleKey === "regular"
                                          ? "font-normal text-[18px]"
                                          : "font-light text-[12px]";
                                const hasChildren =
                                    Array.isArray(subMenu.subMenuList) && subMenu.subMenuList.length > 0;

                                const isStaticLevel2 = subMenu.type === "TEXT" && subMenu.attributes[0].linkUrl === ""; // new rule: TEXT without Level 3 -> static
                                const isOpenL2 = selectedMenu?.id === subMenu.id && hasChildren;
                                return isStaticLevel2 && !hasChildren ? (
                                    <div
                                        id={makeComponentId(2, "item", subMenu.id)}
                                        key={subMenu.id}
                                        className={cn(
                                            weightClass,
                                            "pl-0",
                                            "font-bold dynamic-color whitespace-nowrap truncate",
                                        )}
                                        style={
                                            {
                                                "--dynamic-color": colorStyle?.menuTextNormal || "#3d3d3d",
                                                "--dynamic-hover-color": colorStyle?.menuTextNormal || "#3d3d3d",
                                                "--dynamic-focus-color": colorStyle?.menuTextNormal || "#254b7c",
                                            } as React.CSSProperties
                                        }>
                                        {subMenu.attributes[0].displayName}
                                    </div>
                                ) : (
                                    <div key={subMenu.id} id={makeComponentId(2, "item", subMenu.id)}>
                                        <NavLink
                                            id={makeComponentId(2, "item", subMenu.id)}
                                            href={encodeDynamicParts(subMenu.attributes[0].linkUrl || "")}
                                            target={getLinkTarget(subMenu.linkTarget || "SELF")}
                                            // Click should behave like hover: open Level-3 instead of navigating when children exist
                                            onClick={(e: React.MouseEvent) => {
                                                const hasChildren =
                                                    Array.isArray(subMenu.subMenuList) &&
                                                    (subMenu.subMenuList as WebNavigationMenuWithProducts[]).length > 0;
                                                if (!hasChildren) return; // normal navigation when no children
                                                // For items WITH children: toggle expand/collapse instead of navigate
                                                e.preventDefault();
                                                if (selectedMenu?.id === subMenu.id) {
                                                    // collapse current submenu: keep panel empty (lock auto-select until Level 1 closes)
                                                    setSelectedMenu(null);
                                                    setSelectedSubMenu(null);
                                                    setAutoSelectLocked(true);
                                                    return;
                                                }
                                                // expand new submenu
                                                setSelectedMenu(subMenu);
                                                const children = subMenu.subMenuList as WebNavigationMenuWithProducts[];
                                                setSelectedSubMenu(children);
                                                setAutoSelectLocked(false); // enable future auto-select if user collapses Level 1 entirely
                                                setPanelCache((prev) =>
                                                    prev[subMenu.id]
                                                        ? prev
                                                        : {
                                                              ...prev,
                                                              [subMenu.id]: {
                                                                  menu: subMenu,
                                                                  subMenuList: children,
                                                              },
                                                          },
                                                );
                                            }}
                                            onMouseEnter={() => {
                                                const hasChildren =
                                                    Array.isArray(subMenu.subMenuList) &&
                                                    (subMenu.subMenuList as WebNavigationMenuWithProducts[]).length > 0;
                                                if (!hasChildren) return;
                                                // If different submenu hovered, open it (do not collapse current on same hover)
                                                if (selectedMenu?.id === subMenu.id) return;
                                                setSelectedMenu(subMenu);
                                                const children = subMenu.subMenuList as WebNavigationMenuWithProducts[];
                                                setSelectedSubMenu(children);
                                                setAutoSelectLocked(false);
                                                setPanelCache((prev) =>
                                                    prev[subMenu.id]
                                                        ? prev
                                                        : {
                                                              ...prev,
                                                              [subMenu.id]: { menu: subMenu, subMenuList: children },
                                                          },
                                                );
                                            }}
                                            className={cn(
                                                "flex gap-4 dynamic-color",
                                                level2RowWhenNoL3 ? "w-auto" : "w-full",
                                                selectedMenu &&
                                                    selectedMenu.id === subMenu.id &&
                                                    selectedMenu.type !== "ICON"
                                                    ? "text-start"
                                                    : "text-center",
                                                contentDisplayMode === "TEXT_ONLY"
                                                    ? "items-start text-left"
                                                    : subMenu.type === "ICON" ||
                                                        (subMenu.type === "MASTER" && !!subMenu.attributes[0].imageUrl)
                                                      ? "items-center text-center"
                                                      : "items-start text-left",
                                                computePadding(
                                                    webNavComponentOpts.level2PaddingPreset,
                                                    textStyleKey,
                                                    subMenu.type,
                                                ),
                                                weightClass,
                                            )}
                                            style={
                                                {
                                                    "--dynamic-color":
                                                        selectedMenu?.id === subMenu.id
                                                            ? colorStyle?.menuTextHover || "#254b7c"
                                                            : colorStyle?.menuTextNormal || "#3d3d3d",
                                                    "--dynamic-hover-color": colorStyle?.menuTextHover || "#254b7c",
                                                    "--dynamic-focus-color": colorStyle?.menuTextHover || "#254b7c",
                                                } as React.CSSProperties
                                            }
                                            aria-expanded={hasChildren ? isOpenL2 : undefined}>
                                            {(subMenu.type === "ICON" && !!subMenu.attributes[0].imageUrl) ||
                                            (contentDisplayMode !== "TEXT_ONLY" &&
                                                subMenu.type === "MASTER" &&
                                                !!subMenu.attributes[0].imageUrl) ? (
                                                <div
                                                    className={cn(
                                                        "relative w-auto",
                                                        hasChildren ? "max-w-[227px]" : "max-w-[267px]",
                                                    )}
                                                    id={makeComponentId(2, "image-wrapper", subMenu.id)}>
                                                    <Image
                                                        id={makeComponentId(2, "image", subMenu.id)}
                                                        src={subMenu.attributes[0].imageUrl}
                                                        alt={subMenu.attributes[0].imageAlt}
                                                        width={0}
                                                        height={0}
                                                        style={{ width: "auto", height: "120px" }}
                                                        unoptimized
                                                        loading="lazy"
                                                    />
                                                    <span
                                                        id={makeComponentId(2, "image-caption", subMenu.id)}
                                                        style={
                                                            {
                                                                "--dynamic-color":
                                                                    colorStyle?.menuTextNormal || "#3d3d3d",
                                                                "--dynamic-hover-color":
                                                                    colorStyle?.menuTextHover || "#254b7c",
                                                                "--dynamic-focus-color":
                                                                    colorStyle?.menuTextHover || "#254b7c",
                                                            } as React.CSSProperties
                                                        }
                                                        className={cn(
                                                            "block text-center whitespace-nowrap truncate mt-2 dynamic-color",
                                                            weightClass,
                                                        )}>
                                                        {subMenu.attributes[0].displayName}
                                                    </span>
                                                </div>
                                            ) : (
                                                <div
                                                    className="w-full max-w-[337px]"
                                                    id={makeComponentId(2, "text-wrapper", subMenu.id)}>
                                                    <span
                                                        id={makeComponentId(2, "text", subMenu.id)}
                                                        className={cn(
                                                            "block text-left whitespace-normal break-words dynamic-color",
                                                            weightClass,
                                                        )}
                                                        style={
                                                            {
                                                                display: "-webkit-box",
                                                                WebkitLineClamp: 2,
                                                                WebkitBoxOrient: "vertical" as any,
                                                                overflow: "hidden",
                                                                "--dynamic-color":
                                                                    colorStyle?.menuTextNormal || "#3d3d3d",
                                                                "--dynamic-hover-color":
                                                                    colorStyle?.menuTextHover || "#254b7c",
                                                                "--dynamic-focus-color":
                                                                    colorStyle?.menuTextHover || "#254b7c",
                                                            } as React.CSSProperties
                                                        }>
                                                        {subMenu.attributes[0].displayName}
                                                    </span>
                                                </div>
                                            )}
                                            {hasChildren && (
                                                <span
                                                    className={cn(
                                                        `transition-transform duration-200 ease-in-out self-center align-middle`,
                                                        isOpenL2 && "rotate-180",
                                                    )}
                                                    aria-hidden="true"
                                                    id={makeComponentId(2, "chevron", subMenu.id)}>
                                                    <ChevronRight className="h-4 w-4" />
                                                </span>
                                            )}
                                        </NavLink>
                                    </div>
                                );
                            })}
                        </div>
                    </div>
                    <div
                        id={makeComponentId(3, "panel", menu.id)}
                        className={cn(
                            "nav-level3-panel dynamic-color", // adds background via CSS variable
                            selectedMenu && selectedSubMenu && selectedSubMenu.length > 0
                                ? "transition-all duration-300 ease-in-out py-8 flex opacity-100 translate-x-0 max-h-[1000px] w-full visible pointer-events-auto overflow-y-auto custom-scrollbar"
                                : "transition-none overflow-hidden flex opacity-0 translate-x-8 max-h-0 invisible pointer-events-none overflow-y-auto custom-scrollbar",
                        )}
                        style={
                            {
                                overscrollBehavior: "contain",
                                "--dynamic-color": colorStyle?.menuTextNormal || "#3d3d3d",
                                "--dynamic-hover-color": colorStyle?.menuTextHover || "#254b7c",
                                "--dynamic-focus-color": colorStyle?.menuTextHover || "#254b7c",
                                "--dynamic-nav-level3-bg":
                                    // If a color key is provided by the CMS, pull the color from ColorStyleContext dynamically
                                    webNavComponentOpts.level3BgColorKey &&
                                    (colorStyle as any)?.[(webNavComponentOpts as any).level3BgColorKey]
                                        ? (colorStyle as any)[(webNavComponentOpts as any).level3BgColorKey]
                                        : webNavComponentOpts.customLevel3BgColor || "#FFFFFF",
                            } as React.CSSProperties
                        }>
                        <div
                            className="border-l-[1px] border-gray-200 mr-4 pl-8 h-auto transition-transform duration-200 ease-in-out"
                            id={makeComponentId(3, "divider", menu.id)}
                        />
                        {/* Keep all opened L2 panels mounted; only hide/show them */}
                        {Object.values(panelCache).map(({ menu: cachedMenu, subMenuList: cachedSubMenus }) => (
                            <div
                                key={cachedMenu.id}
                                id={makeComponentId(3, "panel-section", cachedMenu.id)}
                                className={cn(
                                    "flex flex-col w-full gap-4 transition-all duration-300 ease-in-out",
                                    IsIconType && !haveTextType ? "whitespace-pre-line break-words" : "",
                                    cachedMenu.id === selectedMenu?.id
                                        ? "opacity-100 translate-x-0 block"
                                        : "opacity-0 translate-x-8 hidden",
                                )}>
                                {(() => {
                                    const gm = readGeneralOptions(cachedMenu);
                                    if (gm.showSectionTitle === false) return null;
                                    const st = resolveSectionTitleAttributes(
                                        gm,
                                        cachedMenu.attributes[0].displayName,
                                        pathFirst,
                                    );
                                    const cls = sectionTitleWeightClass(st.fontStyle);
                                    const level3Pad =
                                        webNavComponentOpts.level3PaddingPreset === "COMFORTABLE" ? "pl-0" : "pl-2";

                                    const isSectionTitleHaveLink = !!cachedMenu.attributes[0].linkUrl;
                                    return isSectionTitleHaveLink ? (
                                        <NavLink
                                            id="Level3NavCategories"
                                            className={cn(cls, level3Pad, "dynamic-color")}
                                            href={encodeDynamicParts(cachedMenu.attributes[0].linkUrl || "")}
                                            style={
                                                {
                                                    "--dynamic-color": colorStyle?.menuTextNormal || "#3d3d3d",
                                                    "--dynamic-hover-color": colorStyle?.menuTextHover || "#254b7c",
                                                    "--dynamic-focus-color": colorStyle?.menuTextHover || "#254b7c",
                                                } as React.CSSProperties
                                            }
                                            target={getLinkTarget(cachedMenu.linkTarget || "SELF")}>
                                            {st.name}
                                        </NavLink>
                                    ) : (
                                        <span
                                            className={cn(cls, level3Pad, "font-bold dynamic-color")}
                                            style={
                                                {
                                                    "--dynamic-color": colorStyle?.menuTextNormal || "#3d3d3d",
                                                    "--dynamic-hover-color": colorStyle?.menuTextNormal || "#3d3d3d",
                                                    "--dynamic-focus-color": colorStyle?.menuTextNormal || "#254b7c",
                                                } as React.CSSProperties
                                            }>
                                            {st.name}
                                        </span>
                                    );
                                })()}
                                <div className="flex flex-col mt-4" id={makeComponentId(3, "groups", cachedMenu.id)}>
                                    {groupSubMenuByType(cachedSubMenus as WebNavigationMenuWithProducts[]).map(
                                        (group, index) => {
                                            if (group.type === "MASTER") {
                                                return (
                                                    <div
                                                        key={`master-group-${index}`}
                                                        className="flex flex-col gap-4"
                                                        id={makeComponentId(
                                                            3,
                                                            "group-master",
                                                            cachedMenu.id + "-" + index,
                                                        )}>
                                                        {group.items.map((subMenu: WebNavigationMenuWithProducts) => {
                                                            const routeType = (subMenu as any)?.routeType as
                                                                | string
                                                                | undefined;
                                                            const isCollection = routeType === "COLLECTION";
                                                            const collOpts = getCollectionDisplayOptions(subMenu);
                                                            const prodOpts = getProductDisplayOptions(subMenu);
                                                            // New: contentDisplay mode for level-3
                                                            const displayMode = (
                                                                collOpts.contentDisplay ||
                                                                prodOpts.contentDisplay ||
                                                                ""
                                                            ).toUpperCase();
                                                            // Determine per-collection rule for showing products:
                                                            // showProductsInCollection can be:
                                                            //  - boolean: apply to this collection directly
                                                            //  - string[]: list of collection identifiers (param1 or id) for which to show products
                                                            let shouldShowProductsGrid = false;
                                                            const rawCollectionOptions: any =
                                                                (subMenu as any).menuShowOptionCollection ||
                                                                (subMenu as any).menuShowOptionForCollection;
                                                            if (rawCollectionOptions) {
                                                                const rawVal =
                                                                    rawCollectionOptions.showProductsInCollection;
                                                                if (Array.isArray(rawVal)) {
                                                                    const identifier =
                                                                        (subMenu as any).param1 || subMenu.id;
                                                                    shouldShowProductsGrid =
                                                                        rawVal.includes(identifier);
                                                                } else {
                                                                    shouldShowProductsGrid = !!rawVal;
                                                                }
                                                            } else {
                                                                shouldShowProductsGrid =
                                                                    !!collOpts.showProductsInCollection; // fallback (legacy boolean per menu)
                                                            }
                                                            const collThumb = getThumbStyles(
                                                                collOpts.productBorderStyle || "plain",
                                                            );
                                                            const prodThumb = getThumbStyles(
                                                                prodOpts.productBorderStyle || "plain",
                                                            );
                                                            const gridCols = gridColsClassFromNumber(
                                                                collOpts.collectionGridColumns,
                                                            );
                                                            // Determine padding class for collection heading based on preset + collection text style
                                                            const collPadClass = computePadding(
                                                                webNavComponentOpts.level3PaddingPreset,
                                                                collOpts.textStyle,
                                                                subMenu.type,
                                                            );
                                                            const productPadClass = computePadding(
                                                                webNavComponentOpts.level3PaddingPreset,
                                                                prodOpts.textStyle,
                                                                undefined,
                                                            );
                                                            const productGridPadClass =
                                                                (
                                                                    webNavComponentOpts.level3PaddingPreset || "COMPACT"
                                                                ).toUpperCase() === "COMPACT"
                                                                    ? "pl-4"
                                                                    : "pl-0";
                                                            const collStyleKey = (
                                                                collOpts.textStyle || "regular"
                                                            ).toLowerCase();
                                                            const collFontClass =
                                                                collStyleKey === "bold"
                                                                    ? "font-semibold text-[18px]"
                                                                    : collStyleKey === "regular"
                                                                      ? "font-normal text-[18px]"
                                                                      : "font-light text-[12px]";
                                                            const prodStyleKey = (
                                                                prodOpts.textStyle || "regular"
                                                            ).toLowerCase();
                                                            const prodFontClass =
                                                                prodStyleKey === "bold"
                                                                    ? "font-semibold text-[18px]"
                                                                    : prodStyleKey === "regular"
                                                                      ? "font-normal text-[18px]"
                                                                      : "font-light text-[12px]";
                                                            // Intentionally left; ICON/TEXT padding handled inline via computePadding where needed
                                                            return (
                                                                <div
                                                                    key={subMenu.id}
                                                                    id={makeComponentId(3, "container", subMenu.id)}>
                                                                    {isCollection &&
                                                                    subMenu.collectionProductMenus &&
                                                                    subMenu.collectionProductMenus.length > 0 ? (
                                                                        shouldShowProductsGrid ? (
                                                                            <div
                                                                                className="flex flex-col gap-4 mb-4"
                                                                                id={makeComponentId(
                                                                                    3,
                                                                                    "grid-container",
                                                                                    subMenu.id,
                                                                                )}>
                                                                                <NavLink
                                                                                    id={makeComponentId(
                                                                                        3,
                                                                                        "link",
                                                                                        subMenu.id,
                                                                                    )}
                                                                                    style={
                                                                                        {
                                                                                            "--dynamic-color":
                                                                                                colorStyle?.menuTextNormal ||
                                                                                                "#3d3d3d",
                                                                                            "--dynamic-hover-color":
                                                                                                colorStyle?.menuTextHover ||
                                                                                                "#254b7c",
                                                                                            "--dynamic-focus-color":
                                                                                                colorStyle?.menuTextHover ||
                                                                                                "#254b7c",
                                                                                        } as React.CSSProperties
                                                                                    }
                                                                                    href={encodeDynamicParts(
                                                                                        subMenu.attributes[0].linkUrl ||
                                                                                            "",
                                                                                    )}
                                                                                    target={getLinkTarget(
                                                                                        subMenu.linkTarget || "SELF",
                                                                                    )}
                                                                                    className={cn(
                                                                                        "w-fit text-[18px] whitespace-nowrap truncate dynamic-color",
                                                                                        collFontClass,
                                                                                        collPadClass,
                                                                                    )}>
                                                                                    {subMenu.attributes[0].displayName}
                                                                                </NavLink>
                                                                                {displayMode === "TEXT_ONLY" ? (
                                                                                    <div
                                                                                        id={makeComponentId(
                                                                                            3,
                                                                                            "text-container",
                                                                                            subMenu.id,
                                                                                        )}
                                                                                        className={cn(
                                                                                            "flex flex-col gap-2",
                                                                                            productPadClass,
                                                                                        )}>
                                                                                        {(collOpts.limitProductsInCollection
                                                                                            ? subMenu.collectionProductMenus.slice(
                                                                                                  0,
                                                                                                  collOpts.limitProductsInCollection,
                                                                                              )
                                                                                            : subMenu.collectionProductMenus
                                                                                        ).map((productMenu) => (
                                                                                            <NavLink
                                                                                                key={productMenu.id}
                                                                                                href={encodeDynamicParts(
                                                                                                    productMenu
                                                                                                        .attributes[0]
                                                                                                        .linkUrl || "",
                                                                                                )}
                                                                                                style={
                                                                                                    {
                                                                                                        "--dynamic-color":
                                                                                                            colorStyle?.menuTextNormal ||
                                                                                                            "#3d3d3d",
                                                                                                        "--dynamic-hover-color":
                                                                                                            colorStyle?.menuTextHover ||
                                                                                                            "#254b7c",
                                                                                                        "--dynamic-focus-color":
                                                                                                            colorStyle?.menuTextHover ||
                                                                                                            "#254b7c",
                                                                                                    } as React.CSSProperties
                                                                                                }
                                                                                                className={cn(
                                                                                                    "w-fit text-[18px] truncate dynamic-color",
                                                                                                    prodFontClass,
                                                                                                )}
                                                                                                id={makeComponentId(
                                                                                                    3,
                                                                                                    "product-text",
                                                                                                    productMenu.id,
                                                                                                )}>
                                                                                                {
                                                                                                    productMenu
                                                                                                        .attributes[0]
                                                                                                        .displayName
                                                                                                }
                                                                                            </NavLink>
                                                                                        ))}
                                                                                    </div>
                                                                                ) : (
                                                                                    <div
                                                                                        className={cn(
                                                                                            "grid gap-4 items-start max-w-[1000px]",
                                                                                            gridCols,
                                                                                            productGridPadClass,
                                                                                        )}
                                                                                        id={makeComponentId(
                                                                                            3,
                                                                                            "product-grid",
                                                                                            subMenu.id,
                                                                                        )}>
                                                                                        {(collOpts.limitProductsInCollection
                                                                                            ? subMenu.collectionProductMenus.slice(
                                                                                                  0,
                                                                                                  collOpts.limitProductsInCollection,
                                                                                              )
                                                                                            : subMenu.collectionProductMenus
                                                                                        ).map((productMenu) => (
                                                                                            <NavLink
                                                                                                key={productMenu.id}
                                                                                                href={encodeDynamicParts(
                                                                                                    productMenu
                                                                                                        .attributes[0]
                                                                                                        .linkUrl || "",
                                                                                                )}
                                                                                                style={
                                                                                                    {
                                                                                                        "--dynamic-color":
                                                                                                            colorStyle?.menuTextNormal ||
                                                                                                            "#3d3d3d",
                                                                                                        "--dynamic-hover-color":
                                                                                                            colorStyle?.menuTextHover ||
                                                                                                            "#254b7c",
                                                                                                        "--dynamic-focus-color":
                                                                                                            colorStyle?.menuTextHover ||
                                                                                                            "#254b7c",
                                                                                                    } as React.CSSProperties
                                                                                                }
                                                                                                className="flex flex-col items-start gap-2 h-full max-w-[240px] dynamic-color"
                                                                                                id={makeComponentId(
                                                                                                    3,
                                                                                                    "product-card",
                                                                                                    productMenu.id,
                                                                                                )}>
                                                                                                <div
                                                                                                    id={makeComponentId(
                                                                                                        3,
                                                                                                        "image-wrapper",
                                                                                                        productMenu.id,
                                                                                                    )}
                                                                                                    className={
                                                                                                        collThumb.wrapper
                                                                                                    }>
                                                                                                    <Image
                                                                                                        id={makeComponentId(
                                                                                                            3,
                                                                                                            "image",
                                                                                                            productMenu.id,
                                                                                                        )}
                                                                                                        src={
                                                                                                            productMenu
                                                                                                                .attributes[0]
                                                                                                                .imageUrl
                                                                                                        }
                                                                                                        alt={
                                                                                                            productMenu
                                                                                                                .attributes[0]
                                                                                                                .imageAlt
                                                                                                        }
                                                                                                        width={0}
                                                                                                        height={0}
                                                                                                        style={
                                                                                                            collThumb.imgStyle
                                                                                                        }
                                                                                                        className={
                                                                                                            collThumb.imgClass
                                                                                                        }
                                                                                                        unoptimized
                                                                                                        loading="eager"
                                                                                                        priority
                                                                                                    />
                                                                                                </div>
                                                                                                <div
                                                                                                    className="flex-1 text-[18px]"
                                                                                                    id={makeComponentId(
                                                                                                        3,
                                                                                                        "name",
                                                                                                        productMenu.id,
                                                                                                    )}>
                                                                                                    {
                                                                                                        productMenu
                                                                                                            .attributes[0]
                                                                                                            .displayName
                                                                                                    }
                                                                                                </div>
                                                                                            </NavLink>
                                                                                        ))}
                                                                                    </div>
                                                                                )}
                                                                            </div>
                                                                        ) : (
                                                                            <NavLink
                                                                                id={makeComponentId(
                                                                                    3,
                                                                                    "link",
                                                                                    subMenu.id,
                                                                                )}
                                                                                href={encodeDynamicParts(
                                                                                    subMenu.attributes[0].linkUrl || "",
                                                                                )}
                                                                                target={getLinkTarget(
                                                                                    subMenu.linkTarget || "SELF",
                                                                                )}
                                                                                style={
                                                                                    {
                                                                                        "--dynamic-color":
                                                                                            colorStyle?.menuTextNormal ||
                                                                                            "#3d3d3d",
                                                                                        "--dynamic-hover-color":
                                                                                            colorStyle?.menuTextHover ||
                                                                                            "#254b7c",
                                                                                        "--dynamic-focus-color":
                                                                                            colorStyle?.menuTextHover ||
                                                                                            "#254b7c",
                                                                                    } as React.CSSProperties
                                                                                }
                                                                                className={cn(
                                                                                    "w-fit text-[18px] whitespace-nowrap truncate block mb-2 dynamic-color",
                                                                                    collFontClass,
                                                                                    collPadClass,
                                                                                )}>
                                                                                {subMenu.attributes[0].displayName}
                                                                                {collOpts.showTextAndImages &&
                                                                                    subMenu.attributes[0].imageUrl && (
                                                                                        <div
                                                                                            className={cn(
                                                                                                "mt-2",
                                                                                                collThumb.wrapper,
                                                                                            )}
                                                                                            id={makeComponentId(
                                                                                                3,
                                                                                                "image",
                                                                                                subMenu.id,
                                                                                            )}>
                                                                                            <Image
                                                                                                id={makeComponentId(
                                                                                                    3,
                                                                                                    "image",
                                                                                                    subMenu.id,
                                                                                                )}
                                                                                                src={
                                                                                                    subMenu
                                                                                                        .attributes[0]
                                                                                                        .imageUrl
                                                                                                }
                                                                                                alt={
                                                                                                    subMenu
                                                                                                        .attributes[0]
                                                                                                        .imageAlt
                                                                                                }
                                                                                                width={0}
                                                                                                height={0}
                                                                                                style={
                                                                                                    collThumb.imgStyle
                                                                                                }
                                                                                                className={
                                                                                                    collThumb.imgClass
                                                                                                }
                                                                                                unoptimized
                                                                                                loading="eager"
                                                                                                priority
                                                                                            />
                                                                                        </div>
                                                                                    )}
                                                                            </NavLink>
                                                                        )
                                                                    ) : (
                                                                        <NavLink
                                                                            id={makeComponentId(3, "link", subMenu.id)}
                                                                            href={encodeDynamicParts(
                                                                                subMenu.attributes[0].linkUrl || "",
                                                                            )}
                                                                            target={getLinkTarget(
                                                                                subMenu.linkTarget || "SELF",
                                                                            )}
                                                                            style={
                                                                                {
                                                                                    "--dynamic-color":
                                                                                        colorStyle?.menuTextNormal ||
                                                                                        "#3d3d3d",
                                                                                    "--dynamic-hover-color":
                                                                                        colorStyle?.menuTextHover ||
                                                                                        "#254b7c",
                                                                                    "--dynamic-focus-color":
                                                                                        colorStyle?.menuTextHover ||
                                                                                        "#254b7c",
                                                                                } as React.CSSProperties
                                                                            }
                                                                            className={cn(
                                                                                "w-fit text-[18px] whitespace-nowrap truncate block mb-2 dynamic-color",
                                                                                prodFontClass,
                                                                                productPadClass,
                                                                            )}>
                                                                            {subMenu.attributes[0].displayName}
                                                                            {prodOpts.showTextAndImages &&
                                                                                subMenu.attributes[0].imageUrl && (
                                                                                    <div
                                                                                        className={prodThumb.wrapper}
                                                                                        id={makeComponentId(
                                                                                            3,
                                                                                            "image",
                                                                                            subMenu.id,
                                                                                        )}>
                                                                                        <Image
                                                                                            id={makeComponentId(
                                                                                                3,
                                                                                                "image",
                                                                                                subMenu.id,
                                                                                            )}
                                                                                            src={
                                                                                                subMenu.attributes[0]
                                                                                                    .imageUrl
                                                                                            }
                                                                                            alt={
                                                                                                subMenu.attributes[0]
                                                                                                    .imageAlt
                                                                                            }
                                                                                            width={0}
                                                                                            height={0}
                                                                                            style={prodThumb.imgStyle}
                                                                                            className={
                                                                                                prodThumb.imgClass
                                                                                            }
                                                                                            unoptimized
                                                                                            loading="eager"
                                                                                            priority
                                                                                        />
                                                                                    </div>
                                                                                )}
                                                                        </NavLink>
                                                                    )}
                                                                </div>
                                                            );
                                                        })}
                                                    </div>
                                                );
                                            }
                                            if (group.type === "ICON_GROUP") {
                                                return (
                                                    <div
                                                        key={`icon-group-${index}`}
                                                        className={cn(
                                                            "grid grid-cols-4 gap-4 mb-4 max-w-[1000px]",
                                                            computePadding(
                                                                webNavComponentOpts.level3PaddingPreset,
                                                                "regular",
                                                                "ICON_GROUP",
                                                            ),
                                                        )}
                                                        id={makeComponentId(
                                                            3,
                                                            "group-icon",
                                                            cachedMenu.id + "-" + index,
                                                        )}>
                                                        {group.items.map((subMenu) => (
                                                            <NavLink
                                                                key={subMenu.id}
                                                                href={encodeDynamicParts(
                                                                    subMenu.attributes[0].linkUrl || "",
                                                                )}
                                                                target={getLinkTarget(subMenu.linkTarget || "SELF")}
                                                                style={
                                                                    {
                                                                        "--dynamic-color":
                                                                            colorStyle?.menuTextNormal || "#3d3d3d",
                                                                        "--dynamic-hover-color":
                                                                            colorStyle?.menuTextHover || "#254b7c",
                                                                        "--dynamic-focus-color":
                                                                            colorStyle?.menuTextHover || "#254b7c",
                                                                    } as React.CSSProperties
                                                                }
                                                                className="flex flex-col gap-2 dynamic-color"
                                                                id={makeComponentId(3, "icon-item", subMenu.id)}>
                                                                <div
                                                                    className="flex relative w-full max-w-[240px] p-2 justify-center h-auto bg-white rounded-[8px] border border-gray-100"
                                                                    id={makeComponentId(3, "image", subMenu.id)}>
                                                                    <Image
                                                                        id={makeComponentId(3, "image", subMenu.id)}
                                                                        src={subMenu.attributes[0].imageUrl}
                                                                        alt={subMenu.attributes[0].imageAlt}
                                                                        width={0}
                                                                        height={0}
                                                                        style={{ width: "auto", height: "120px" }}
                                                                        className="object-contain"
                                                                        unoptimized
                                                                        loading="eager"
                                                                    />
                                                                </div>
                                                                <div
                                                                    className="flex-1 text-start text-[18px] max-w-[240px] whitespace-normal break-words"
                                                                    style={{
                                                                        display: "-webkit-box",
                                                                        WebkitLineClamp: 3,
                                                                        WebkitBoxOrient: "vertical",
                                                                        overflow: "hidden",
                                                                    }}
                                                                    id={makeComponentId(3, "name", subMenu.id)}>
                                                                    {subMenu.attributes[0].displayName}
                                                                </div>
                                                            </NavLink>
                                                        ))}
                                                    </div>
                                                );
                                            }
                                            if (group.type === "TEXT") {
                                                return group.items.map((subMenu) => {
                                                    const link = subMenu.attributes[0].linkUrl || "";
                                                    const label = subMenu.attributes[0].displayName;

                                                    const isStaticText = link === "";
                                                    // Determine padding via collection/product textStyle if available

                                                    // COMFORT pl-0
                                                    // COMPACT {
                                                    //        link === "" ? pl-2 : pl-4    
                                                    //         }
                                                    const textPadClass =
                                                        webNavComponentOpts.level3PaddingPreset === "COMFORTABLE"
                                                            ? "pl-0"
                                                            : isStaticText
                                                            ? "pl-2"
                                                            : "pl-4";

                                                    return isStaticText ? (
                                                        <div
                                                            id={makeComponentId(3, "name", subMenu.id)}
                                                            key={subMenu.id}
                                                            className={cn(
                                                                "text-[18px] font-bold dynamic-color mb-2",
                                                                textPadClass,
                                                            )}
                                                            style={
                                                                {
                                                                    "--dynamic-color":
                                                                        colorStyle?.menuTextNormal || "#3d3d3d",
                                                                    "--dynamic-hover-color":
                                                                        colorStyle?.menuTextNormal || "#3d3d3d",
                                                                    "--dynamic-focus-color":
                                                                        colorStyle?.menuTextNormal || "#254b7c",
                                                                } as React.CSSProperties
                                                            }>
                                                            {label}
                                                        </div>
                                                    ) : (
                                                        <NavLink
                                                            key={subMenu.id}
                                                            href={encodeDynamicParts(link)}
                                                            target={getLinkTarget(subMenu.linkTarget || "SELF")}
                                                            style={
                                                                {
                                                                    "--dynamic-color":
                                                                        colorStyle?.menuTextNormal || "#3d3d3d",
                                                                    "--dynamic-hover-color":
                                                                        colorStyle?.menuTextHover || "#254b7c",
                                                                    "--dynamic-focus-color":
                                                                        colorStyle?.menuTextHover || "#254b7c",
                                                                } as React.CSSProperties
                                                            }
                                                            className={cn(
                                                                "w-fit text-[18px] font-normal whitespace-nowrap truncate block mb-2 dynamic-color",
                                                                textPadClass,
                                                            )}
                                                            id={makeComponentId(3, "text-link", subMenu.id)}>
                                                            <div id={makeComponentId(3, "text-label", subMenu.id)}>
                                                                {label}
                                                            </div>
                                                        </NavLink>
                                                    );
                                                });
                                            }

                                            return null;
                                        },
                                    )}
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            </NavigationMenu.Content>
        </NavigationMenu.Item>
    );
};
